import {useEffect, useMemo, useState} from 'react'
import {ApplePay as AdyenApplePay} from '@adyen/adyen-web'
import logger from '../utils/logger-instance'
import {useCountry} from './use-country'

/**
 * @param {Object} checkout - Adyen checkout instance
 * @param {Object} [config] - optional ApplePay configuration
 * @param {boolean} [enabled] - optional flag to enable/disable ApplePay
 * @returns {null | {applePay: any, checkout: any, config: any}}
 */
const useApplePay = ({checkout, config, enabled}) => {
    const country = useCountry()

    // keep the config and checkout in the state to avoid re-creating the instance when they are the same
    /**
     * @type {{applePay: any, checkout: any, config: any} | null}
     */
    const [applePayState, setApplePayState] = useState(null)

    // prepare ApplePay configuration based on the provided config
    const applePayConfig = useMemo(
        () => ({
            // sensible defaults
            buttonType: 'plain',
            buttonColor: 'white-with-line',
            countryCode: country,
            showPayButton: true,
            isExpress: true,
            requiredBillingContactFields: ['postalAddress'],
            requiredShippingContactFields: ['postalAddress', 'name', 'phone', 'email'],
            instantPaymentTypes: ['applepay'],
            ...config
        }),
        [country, config]
    )

    useEffect(() => {
        if (checkout && enabled !== false) {
            // skip if the checkout and config are the same
            if (applePayState?.checkout === checkout && applePayState?.config === applePayConfig) {
                return
            }
            // clear any previous state
            setApplePayState(null)

            const applePay = new AdyenApplePay(checkout, applePayConfig)
            let unmounted = false
            applePay.isAvailable().then(
                () => {
                    if (unmounted) {
                        return
                    }
                    setApplePayState({
                        applePay,
                        checkout,
                        config: applePayConfig
                    })
                },
                (e) => {
                    setApplePayState(null)
                    logger.warn('ApplePay cannot be initialized', e)
                }
            )
        } else {
            setApplePayState(null)
            return
        }
    }, [applePayConfig, applePayState, checkout, enabled])

    return applePayState || {}
}

export default useApplePay
